import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../ui/sheet';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import {
  UserPlus,
  CalendarDays,
  Loader2,
  CheckCircle,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';

// Time picker options
const timeOptions = [];
for (let h = 6; h <= 20; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    timeOptions.push(`${hour}:${minute}`);
  }
}

export const ServiceAssignmentPanel = ({ order, specialists, onAssign, isOpen, onClose, title, isReassign = false }) => {
  const { t, i18n } = useTranslation('orders');
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  
  const [bulkSpecialist, setBulkSpecialist] = useState('');
  const [bulkDate, setBulkDate] = useState(null);
  const [bulkTime, setBulkTime] = useState('');
  const [bulkDateOpen, setBulkDateOpen] = useState(false);

  const dateLocale = i18n.language === 'en' ? enUS : es;
  const panelTitle = title || (isReassign ? t('assign.reassignTitle') : t('assign.title'));

  useEffect(() => {
    if (order?.services) {
      const initial = {};
      order.services.forEach(svc => {
        initial[svc.id] = {
          specialist_id: svc.assigned_specialist_id || '',
          date: svc.scheduled_date ? new Date(svc.scheduled_date) : null,
          time: svc.scheduled_time || '',
          dateOpen: false
        };
      });
      setAssignments(initial);
    }
  }, [order]);

  const updateServiceAssignment = (serviceId, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value
      }
    }));
  };

  const getEditableServices = () => {
    if (!order?.services) return [];
    if (isReassign) {
      return order.services.filter(s => s.status !== 'completed');
    }
    return order.services.filter(s => s.status === 'pending' || !s.assigned_specialist_id);
  };

  const applyBulkAssignment = () => {
    if (!bulkSpecialist) {
      toast.error(t('assign.selectAtLeastOne'));
      return;
    }
    
    const updated = { ...assignments };
    const editableServices = getEditableServices();
    
    editableServices.forEach(svc => {
      updated[svc.id] = {
        ...updated[svc.id],
        specialist_id: bulkSpecialist,
        date: bulkDate,
        time: bulkTime
      };
    });
    setAssignments(updated);
    toast.success(t('assign.specialistApplied', { count: editableServices.length }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const toAssign = [];
      
      Object.entries(assignments).forEach(([serviceId, assignment]) => {
        if (assignment.specialist_id) {
          toAssign.push({
            service_item_id: serviceId,
            specialist_id: assignment.specialist_id,
            scheduled_date: assignment.date ? format(assignment.date, 'yyyy-MM-dd') : null,
            scheduled_time: assignment.time || null
          });
        }
      });

      if (toAssign.length === 0) {
        toast.error(t('assign.selectAtLeastOne'));
        setLoading(false);
        return;
      }

      for (const assignment of toAssign) {
        await onAssign(order.id, assignment);
      }
      
      toast.success(isReassign 
        ? t('assign.servicesReassigned', { count: toAssign.length })
        : t('assign.servicesAssigned', { count: toAssign.length })
      );
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('messages.errorAssigning'));
    }
    setLoading(false);
  };

  const editableServices = getEditableServices();
  const activeSpecialists = specialists?.filter(s => s.status === 'active') || [];

  const getSpecialistName = (id) => {
    const spec = specialists?.find(s => s.id === id);
    return spec ? `${spec.first_name} ${spec.last_name}` : '';
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[500px] sm:max-w-[500px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b bg-muted">
          <SheetTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {panelTitle}
          </SheetTitle>
          <SheetDescription>
            {t('table.patient')}: <span className="font-medium text-foreground">{order?.patient_name}</span>
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="py-4 space-y-6">
            {/* Bulk Assignment Section */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                {isReassign ? t('assign.quickReassign') : t('assign.quickAssign')}
                <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                  {editableServices.length} {t('common:services', 'servicio(s)')}
                </Badge>
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {isReassign ? t('assign.quickReassignDesc') : t('assign.quickAssignDesc')}
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">{t('assign.specialist')}</Label>
                  <Select value={bulkSpecialist} onValueChange={setBulkSpecialist}>
                    <SelectTrigger className="mt-1 h-10 bg-background">
                      <SelectValue placeholder={t('assign.selectSpecialist')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeSpecialists.map((spec, index) => (
                        <SelectItem key={spec.id || `spec-${index}`} value={spec.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
                              {spec.first_name?.[0]}{spec.last_name?.[0]}
                            </div>
                            {spec.first_name} {spec.last_name}
                            {spec.specialty && <span className="text-muted-foreground">- {spec.specialty}</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">{t('assign.date')}</Label>
                    <Popover open={bulkDateOpen} onOpenChange={setBulkDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="mt-1 w-full h-10 justify-start text-left font-normal bg-background"
                        >
                          <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />
                          {bulkDate ? format(bulkDate, 'PPP', { locale: dateLocale }) : t('assign.selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={bulkDate}
                          onSelect={(date) => {
                            setBulkDate(date);
                            setBulkDateOpen(false);
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">{t('assign.time')}</Label>
                    <Select value={bulkTime} onValueChange={setBulkTime}>
                      <SelectTrigger className="mt-1 h-10 bg-background">
                        <SelectValue placeholder={t('assign.time')} />
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.map((time) => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={applyBulkAssignment}
                  disabled={!bulkSpecialist || editableServices.length === 0}
                >
                  {t('assign.applyToServices', { count: editableServices.length })}
                </Button>
              </div>
            </div>

            <Separator />

            {/* Individual Services */}
            <div>
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                {t('assign.orderServices')}
                <Badge variant="outline" className="ml-auto">
                  {order?.services?.length || 0} {t('assign.total')}
                </Badge>
              </h4>

              <div className="space-y-3">
                {order?.services?.map((service, index) => {
                  const assignment = assignments[service.id] || {};
                  const isEditable = isReassign 
                    ? service.status !== 'completed'
                    : (service.status === 'pending' || !service.assigned_specialist_id);
                  const isCompleted = service.status === 'completed';

                  return (
                    <div 
                      key={service.id || `service-${index}`}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        isCompleted ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' :
                        assignment.specialist_id ? 'bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800' : 
                        'bg-card border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-foreground text-sm">
                            {service.service_name || t('common:serviceNoName', 'Servicio sin nombre')}
                          </h5>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t('common:code', 'Codigo')}: {service.service_code || 'N/A'} - {t('common:quantity', 'Cantidad')}: {service.quantity || 1}
                            {service.frequency && ` - ${service.frequency}`}
                          </p>
                        </div>
                        <Badge className={`text-xs ${
                          service.status === 'completed' ? 'bg-green-100 text-green-800' :
                          service.status === 'assigned' ? 'bg-cyan-100 text-cyan-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(`serviceStatus.${service.status || 'pending'}`, service.status || 'Pending')}
                        </Badge>
                      </div>

                      {service.assigned_specialist_name && !isEditable && (
                        <div className="flex items-center gap-2 text-sm text-cyan-700 dark:text-cyan-300 bg-cyan-100/50 dark:bg-cyan-900/30 rounded-lg px-3 py-2 mb-3">
                          <CheckCircle className="h-4 w-4" />
                          <span>{t('assign.assignedTo')} <strong>{service.assigned_specialist_name}</strong></span>
                        </div>
                      )}

                      {isEditable && (
                        <div className="space-y-3 pt-2 border-t border-border/50">
                          {isReassign && service.assigned_specialist_name && (
                            <div className="text-xs text-muted-foreground bg-muted rounded px-2 py-1">
                              {t('assign.currentSpecialist')}: {service.assigned_specialist_name}
                            </div>
                          )}
                          
                          <div>
                            <Label className="text-xs font-medium text-muted-foreground">
                              {isReassign ? t('assign.newSpecialist') : t('assign.specialistForService')}
                            </Label>
                            <Select 
                              value={assignment.specialist_id || ''} 
                              onValueChange={(v) => updateServiceAssignment(service.id, 'specialist_id', v)}
                            >
                              <SelectTrigger className="mt-1 h-9 bg-background text-sm">
                                <SelectValue placeholder={t('assign.selectSpecialist')} />
                              </SelectTrigger>
                              <SelectContent>
                                {activeSpecialists.map((spec, index) => (
                                  <SelectItem key={spec.id || `spec-individual-${index}`} value={spec.id}>
                                    {spec.first_name} {spec.last_name}
                                    {spec.specialty && <span className="text-muted-foreground ml-1">({spec.specialty})</span>}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs font-medium text-muted-foreground">{t('assign.date')}</Label>
                              <Popover 
                                open={assignment.dateOpen} 
                                onOpenChange={(open) => updateServiceAssignment(service.id, 'dateOpen', open)}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className="mt-1 w-full h-9 justify-start text-left font-normal text-sm bg-background"
                                  >
                                    <CalendarDays className="mr-2 h-3 w-3 text-muted-foreground" />
                                    {assignment.date ? format(assignment.date, 'dd/MM/yyyy') : t('assign.date')}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={assignment.date}
                                    onSelect={(date) => {
                                      updateServiceAssignment(service.id, 'date', date);
                                      updateServiceAssignment(service.id, 'dateOpen', false);
                                    }}
                                    disabled={(date) => date < new Date()}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div>
                              <Label className="text-xs font-medium text-muted-foreground">{t('assign.time')}</Label>
                              <Select 
                                value={assignment.time || ''} 
                                onValueChange={(v) => updateServiceAssignment(service.id, 'time', v)}
                              >
                                <SelectTrigger className="mt-1 h-9 bg-background text-sm">
                                  <SelectValue placeholder={t('assign.time')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeOptions.map((time) => (
                                    <SelectItem key={time} value={time}>{time}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {assignment.specialist_id && (
                            <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 rounded px-2 py-1">
                              <CheckCircle className="h-3 w-3" />
                              {isReassign ? t('assign.willReassign') : t('assign.willAssign')}: {getSpecialistName(assignment.specialist_id)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="px-6 py-4 border-t bg-muted">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common:cancel', 'Cancelar')}
          </Button>
          <Button 
            onClick={handleSave}
            className="flex-1 bg-primary hover:bg-primary/90"
            disabled={loading}
            data-testid="confirm-assign-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            {isReassign ? t('assign.saveReassignments') : t('assign.saveAssignments')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ServiceAssignmentPanel;
